import React, { useEffect, useRef, useState } from 'react';
import API from '../../API';
import { IntersectSign, parseIntersectSign, validateIntersectSignFile, prepareIntersectSignFile, SignFit, SIGN_BACKGROUND } from './intersectSign';

export default function IntersectSignEditor({ accessToken, eventId }: { accessToken: string; eventId: string }) {
  const [sign, setSign] = useState<IntersectSign | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [fit, setFit] = useState<SignFit>('contain');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const operation = useRef(0);
  const input = useRef<HTMLInputElement>(null);
  const busyRef = useRef(false);
  const load = async () => {
    const ticket = ++operation.current;
    setError(''); setStatus(''); setFile(null); setConfirmReset(false); setSign(null);
    if (input.current) input.current.value = '';
    try {
      const response = await API.getIntersectSign(accessToken, { eventId });
      if (!response.ok) throw new Error('Could not load the IntersectSign.');
      const data = parseIntersectSign(await response.json());
      if (ticket === operation.current) setSign(data);
    } catch (e) { if (ticket === operation.current) setError((e as Error).message); }
  };
  useEffect(() => {
    busyRef.current = false; setBusy(false);
    void load();
    return () => { ++operation.current; };
    // Component is remounted when the moderator panel closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, eventId]);
  useEffect(() => {
    if (!file) { setPreview(''); return; }
    const url = URL.createObjectURL(file); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const select = async (candidate?: File) => {
    if (!candidate || busyRef.current) return;
    const ticket = ++operation.current;
    setFile(null); setError(''); setStatus(''); setConfirmReset(false);
    try {
      await validateIntersectSignFile(candidate);
      if (ticket === operation.current) setFile(candidate);
    } catch (e) { if (ticket === operation.current) setError((e as Error).message); }
  };
  const save = async (reset: boolean) => {
    if (!sign || busyRef.current || (!reset && !file)) return;
    const ticket = ++operation.current;
    busyRef.current = true; setBusy(true); setError(''); setStatus('');
    try {
      const image = !reset && file ? await prepareIntersectSignFile(file, fit) : null;
      if (ticket !== operation.current) return;
      const response = reset
        ? await API.resetIntersectSign(accessToken, { eventId, version: sign.version })
        : await API.uploadIntersectSign(accessToken, { eventId, version: sign.version, image });
      if (!response.ok) throw new Error((await response.text()).trim() || 'Could not save the IntersectSign.');
      const updated = parseIntersectSign(await response.json());
      // Refresh other mounted viewers even if the moderator closed the panel during the upload.
      window.dispatchEvent(new CustomEvent('intersectSignUpdated', { detail: { eventId, sign: updated } }));
      if (ticket === operation.current) {
        setSign(updated); setFile(null); setConfirmReset(false);
        if (input.current) input.current.value = '';
        setStatus(reset ? 'Original scene artwork restored.' : 'Saved. Everyone’s sign will update shortly.');
      }
    } catch (e) { if (ticket === operation.current) setError((e as Error).message); }
    finally { if (ticket === operation.current) { busyRef.current = false; setBusy(false); } }
  };
  return <section aria-labelledby="intersect-sign-heading" className="space-y-3 rounded-lg border border-purple-500/40 p-3 text-white">
    <h4 id="intersect-sign-heading" className="font-semibold">IntersectSign</h4>
    <p className="text-xs text-gray-300">PNG or JPEG · any aspect ratio · max 4 MiB and 32 megapixels. Saved at 2500 × 1144 pixels; the size in the world stays unchanged. Replaces the sign for everyone.</p>
    {!sign && !error && <p role="status">Loading sign…</p>}
    {sign && <>
      {preview || sign.url ? <img crossOrigin="anonymous" src={preview || sign.url} alt={preview ? 'New IntersectSign preview' : 'Current IntersectSign'} className="w-full rounded" style={{ aspectRatio: '2500 / 1144', backgroundColor: SIGN_BACKGROUND, objectFit: preview ? fit : 'contain' }} /> : <p className="text-sm text-gray-300">Using the original artwork from the scene.</p>}
      <label className="block text-sm">Replacement image
        <input ref={input} type="file" accept="image/png,image/jpeg" disabled={busy} onChange={e => void select(e.target.files?.[0])} className="mt-1 block w-full text-xs" />
      </label>
      {file && <label className="block text-sm">Image layout
        <select value={fit} disabled={busy} onChange={e => setFit(e.target.value as SignFit)} className="mt-1 block w-full rounded bg-gray-800 p-2">
          <option value="contain">Fit — show entire image with dark padding</option>
          <option value="cover">Fill — crop edges to cover the sign</option>
          <option value="fill">Stretch — fill without cropping (may distort)</option>
        </select>
      </label>}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy || !file} onClick={() => void save(false)} className="rounded bg-purple-700 px-3 py-2 text-sm disabled:opacity-40">{busy ? 'Saving…' : 'Upload and apply'}</button>
        <button type="button" disabled={busy || !sign.url} onClick={() => setConfirmReset(true)} className="rounded border border-gray-600 px-3 py-2 text-sm disabled:opacity-40">Restore original</button>
      </div>
      {confirmReset && <div className="text-sm"><p>Restore the scene’s original IntersectSign for everyone?</p><button type="button" disabled={busy} onClick={() => void save(true)} className="mr-3 underline">Confirm restore</button><button type="button" disabled={busy} onClick={() => setConfirmReset(false)}>Cancel</button></div>}
    </>}
    {status && <p role="status" className="text-sm text-green-300">{status}</p>}
    {error && <div><p role="alert" className="text-sm text-red-300">{error}</p><button type="button" disabled={busy} onClick={() => void load()} className="text-sm underline">Reload saved sign</button></div>}
  </section>;
}
